type PaymentFrequency = 'daily' | 'weekly' | 'fortnightly' | 'monthly';

interface LoanData {
  loanAmount: number;
  loanTerm: number;
  loanTermType: 'days' | 'months';
  factorRate: number;
  paymentFrequency: PaymentFrequency;

  adminFee?: number;
  riskAssessmentFee?: number;
  dischargeFee?: number;
  loanGuaranteeFee?: number;
  otherFees?: number;
  monthlyFee?: number;
}

function rate(nper: number, pmt: number, pv: number, fv = 0, type = 0, guess = 0.2): number {
  const FINANCIAL_MAX_ITERATIONS = 128;
  const FINANCIAL_PRECISION = 1.0e-8;

  let r = guess;
  let y, f, y0, y1, x0, x1;
  let i = 0;

  if (Math.abs(r) < FINANCIAL_PRECISION) {
    y = pv * (1 + nper * r) + pmt * (1 + r * type) * nper + fv;
  } else {
    f = Math.exp(nper * Math.log(1 + r));
    y = pv * f + pmt * (1 / r + type) * (f - 1) + fv;
  }
  y0 = pv + pmt * nper + fv;
  y1 = y;
  x0 = 0;
  x1 = r;

  while (Math.abs(y0 - y1) > FINANCIAL_PRECISION && i < FINANCIAL_MAX_ITERATIONS) {
    r = (y1 * x0 - y0 * x1) / (y1 - y0);
    x0 = x1;
    x1 = r;

    if (Math.abs(r) < FINANCIAL_PRECISION) {
      y = pv * (1 + nper * r) + pmt * (1 + r * type) * nper + fv;
    } else {
      f = Math.exp(nper * Math.log(1 + r));
      y = pv * f + pmt * (1 / r + type) * (f - 1) + fv;
    }

    y0 = y1;
    y1 = y;
    i++;
  }
  return r;
}

export function useFactorToAPR(data: LoanData) {
  const clean = (v?: number) => (v && !isNaN(v) ? v : 0);

  const loanAmount = clean(data.loanAmount);
  const loanTerm = clean(data.loanTerm);
  const factorRate = clean(data.factorRate);
  const adminFee = clean(data.adminFee);
  const riskAssessmentFee = clean(data.riskAssessmentFee);
  const dischargeFee = clean(data.dischargeFee);
  const loanGuaranteeFee = clean(data.loanGuaranteeFee);
  const otherFees = clean(data.otherFees);
  const monthlyFee = clean(data.monthlyFee);

  if (!loanAmount || !loanTerm || !factorRate || !data.paymentFrequency) {
    return null;
  }

  // Term in days and months (for fee schedule: ~30.44 days/month)
  const AVG_MONTH_DAYS = 365 / 12;
  const termInDays = data.loanTermType === 'days' ? loanTerm : loanTerm * AVG_MONTH_DAYS;
  const termInMonths = data.loanTermType === 'months' ? loanTerm : loanTerm / AVG_MONTH_DAYS;

  // 1. Factor payback & interest: loan × factor = payback, interest = payback - loan
  const payback = loanAmount * factorRate;
  const interest = payback - loanAmount;

  // 2. Simple annualised interest rate: (interest/loan)×365÷termInDays → %
  const interestRate = (interest / loanAmount) * (365 / termInDays) * 100;

  // 3. Payments per year and number of payments over term (daily 260, weekly 52, fortnightly 26, monthly 12)
  const paymentsPerYearMap: Record<PaymentFrequency, number> = {
    daily: 260,
    weekly: 52,
    fortnightly: 26,
    monthly: 12,
  };
  const paymentsPerYear = paymentsPerYearMap[data.paymentFrequency];
  const numPaymentsFloat = (paymentsPerYear * termInDays) / 365;
  // Round up to ensure we don't under-count payments (e.g. 128.219 → 129)
  const numPayments = Math.max(1, Math.ceil(numPaymentsFloat));
  const paymentsPerMonth = paymentsPerYear / 12;

  // Repayment amount = total payback ÷ number of payments
  const repaymentAmount = payback / numPayments;

  // Rate per period from amortisation (IRR) — e.g. 0.42% per day → decimal 0.0042
  const simpleRateGuess =
    numPayments > 0 ? (payback - loanAmount) / loanAmount / numPayments : 0.01;
  const rateGuess = Math.min(0.2, Math.max(1e-6, simpleRateGuess));
  const ratePerPeriod = rate(numPayments, -repaymentAmount, loanAmount, 0, 0, rateGuess);

  // Effective APR = rate per period (as %) × payments per year [e.g. 0.42% × 260 = 110.12%]
  const effectiveAPR = ratePerPeriod * 100 * paymentsPerYear;

  // 4. Fees over loan term (one-off + monthlyFee × termInMonths)
  const totalFeesReal =
    adminFee +
    riskAssessmentFee +
    dischargeFee +
    loanGuaranteeFee +
    otherFees +
    monthlyFee * termInMonths;

  // Fees as APR: (totalFees/loan)×365÷termInDays → % [e.g. 620/20000×365/180 = 6.29%]
  const costOfFeesAsApr = (totalFeesReal / loanAmount) * (365 / termInDays) * 100;

  // Final effective APR = effective APR from schedule + fee equivalent
  const apr = effectiveAPR + costOfFeesAsApr;

  const totalCost = payback + totalFeesReal;
  const totalInterestFees = interest + totalFeesReal;

  return {
    principle: loanAmount.toFixed(2),
    APR: apr.toFixed(2),
    totalCost: totalCost.toFixed(2),
    interestRate: interestRate.toFixed(2),
    totalInterestFees: totalInterestFees.toFixed(2),
    repaymentAmount: repaymentAmount.toFixed(2),
    numPayments,

    calculationDetails: {
      termInDays,
      termInMonths,
      paymentsPerYear,
      paymentsPerMonth,
      numPayments,
      payback,
      interest,
      ratePerPeriod,
      ratePerPeriodPercent: (ratePerPeriod * 100).toFixed(2),
      effectiveAPR: effectiveAPR.toFixed(2),
      costOfFeesAsApr: costOfFeesAsApr.toFixed(2),
      totalFeesReal,
    },
  };
}
